import { FC, useState } from 'react'
import { InputContainer, InputLabel, InputBox, Input, CurrencyIcon } from './styles'
import { NumberFormatPropsBase, FormatInputValueFunction } from 'react-number-format'
import { HiOutlineCalendar } from 'react-icons/hi'
import { ValidationError, useErrors } from 'src/context/errors'

type MaskStyleTypes = 'phone' | 'date' | 'percentage' | 'monthYear' | 'number' | 'postalCode'

type LeftIconTypes = 'calendar' | 'currency'

interface StandardFormatInputProps extends NumberFormatPropsBase<unknown> {
  label: string;
  maskStyle: MaskStyleTypes;
  labelSize?: 'small' | 'high';
  leftIcon?: LeftIconTypes;
  disabled?: boolean;
  isIncorrect?: boolean;
  errorMessage?: string;
  onBlur?: Function;
  errorCode?: string;
}

const fillFromRightToLeft = (value: string) => {
  if (!Number(value)) return '';

  let amount = '';
  if (amount.length > 1) {
    amount = parseInt(value).toFixed(1);
  } else {
    amount = (parseInt(value) / 10).toFixed(1);
  }

  return `${amount}`;
}

const formats: {[prop in MaskStyleTypes]: string | undefined | FormatInputValueFunction} = {
  date: '##/##/####',
  monthYear: '##/####',
  phone: '(###)-###-####',
  percentage: fillFromRightToLeft,
  number: undefined,
  postalCode: '#########'
}

const placeholders: {[prop in MaskStyleTypes]: string | undefined} = {
  date: 'MM/DD/YYYY',
  monthYear: 'MM/YYYY',
  phone: '(555)-555-5555',
  percentage: '0.0',
  number: undefined,
  postalCode: undefined
}

const LeftIcon = ({ icon }: { icon: LeftIconTypes }) => {
  switch(icon) {
    case 'calendar': return <HiOutlineCalendar />
    case 'currency': return <CurrencyIcon>$</CurrencyIcon>
  }
}

const StandardFormatInput: FC<StandardFormatInputProps> = ({
  label,
  labelSize,
  maskStyle,
  leftIcon,
  disabled,
  isIncorrect,
  errorMessage,
  errorCode = '',
  onBlur,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const { hasError, getErrorMessage } = useErrors()

  isIncorrect = hasError(errorCode) || isIncorrect 
  errorMessage = getErrorMessage(errorCode) || errorMessage

  return (
    <InputContainer>
      <InputLabel labelSize={labelSize ?? 'small'} isIncorrect={isIncorrect}>{label}</InputLabel>
      <InputBox isIncorrect={isIncorrect === true} isFocused={isFocused}>
        {!!leftIcon ? <LeftIcon icon={leftIcon} /> : null}
        <Input
          data-error-code={errorCode}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur && onBlur(e);
          }}
          format={formats[maskStyle]}
          placeholder={placeholders[maskStyle]}
          disabled={disabled}
          {...rest}
        />
        {maskStyle === 'percentage' ? <span>%</span> : null}
      </InputBox>
      {!!errorMessage ? (
      <small style={{ whiteSpace: 'normal' }}>{errorMessage}</small>
      ) : null}
    </InputContainer>
  )
}

export default StandardFormatInput
